<script setup>
import { HrIcon } from '../primitives';
import HrListItem from './HrListItem.vue';

/**
 * Add-item button for a list — a flat, accent-colored action row that adds an entry at the end of
 * a list (e.g. beneath HrSortableList / HrListItem rows).
 *
 * It aligns to the list's columns — the leading `+` sits in the avatar column (in line with row
 * leading icons / drag handles) and the label sits in the content column (in line with row titles)
 * — so it reads as a continuation of the list. It stays deliberately flat: no fill and no
 * elevation, only a rounded hover state layer. Against filled/segmented rows (where fill signals
 * "a record with data"), the flat treatment reads as "an action, not a record", and its base-surface
 * elevation sits correctly below the raised tiles.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Adding a new item at the end of a list, aligned to the rows' columns
 * @avoidWhen A standalone action not tied to a list's column layout — use HrSubtleBtn
 */
defineOptions({ name: 'HrListAddBtn' });

defineProps({
  /**
   * Prevents activation and dims the row.
   *
   * Declared rather than left to attribute fallthrough, which reached HrListItem without ever being
   * forwarded to it as a prop. HrListItem is what reconciles `disable` with `clickable`; this only
   * has to pass it on.
   */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Removes the horizontal inset so the button sits flush with its container.
   *
   * The inset exists to keep the `+` aligned with inset segmented tiles. A list whose tiles are
   * `flush` needs this too, or the button and the rows it belongs to disagree by 8px.
   */
  flush: {
    type: Boolean,
    default: false,
  },
  /** Button text, e.g. "Add diagnosis". */
  label: {
    type: String,
    required: true,
  },
  /** Leading icon; sits in the avatar column so it lines up with row leading icons/handles. */
  icon: {
    type: String,
    default: 'add',
  },
  /**
   * Footprint. `flat` is a compact action row that caps a populated list. `outlined` is a
   * tile-shaped, outlined empty slot that occupies the group when there are no items yet — it
   * reads as "a place a tile will go" and doubles as the empty state. Both are the same trigger,
   * still with no fill.
   * @values flat, outlined
   */
  variant: {
    type: String,
    default: 'flat',
    validator: (v) => ['flat', 'outlined'].includes(v),
  },
});

const emit = defineEmits({
  /** Fired when the button is activated. */
  click: null,
});
</script>

<template>
  <hr-list-item
    :label="label"
    clickable
    :disable="disable"
    :class="['hr-list-add-btn', `hr-list-add-btn--${variant}`, { 'hr-list-add-btn--flush': flush }]"
    @click="emit('click', $event)"
  >
    <template #leading>
      <hr-icon
        :name="icon"
        class="hr-list-add-btn__icon"
      />
    </template>
  </hr-list-item>
</template>

<style lang="scss" scoped>
/*
 * Flat action beneath the records: transparent (no tile fill or elevation) with only a rounded
 * hover state layer. Vertical margin only, matching a segmented tile, so the `+` stays in the same
 * column as the rows' leading elements whatever inset the container around them chooses.
 */
.hr-list-add-btn {
  margin: 2px 0;
  border-radius: var(--app-shape-corner-medium);
  overflow: hidden;

  /* BOTH, because the label takes its color from one of them in each theme. Themed, HrItemLabel
     sets its own color from the variable and `color` never reaches it, leaving the accent on the
     icon alone; in legacy that class is absent and the label inherits `color` instead. */
  color: var(--app-primary);
  --hr-on-container: var(--app-primary);
}

/*
 * LEGACY HAS ONE DIALECT FOR ACTIONS — the uppercase flat button — and this row does not speak it:
 * its label stays a sentence-case item label, so the row reads lighter than the inline buttons
 * nested inside the very list it caps. Quasar's q-btn metrics, matched so both adds carry one type
 * role and footprint alone orders them. Themed, HrItemLabel owns the type and none of this applies.
 */
html:not([data-theme]) .hr-list-add-btn :deep(.q-item__label) {
  font-size:   var(--app-type-label-large-size);
  line-height: var(--app-type-label-large-line-height);
  font-weight: var(--app-type-label-large-weight);
  /* q-btn's own tracking rather than the token's 0.1px, or the two labels disagree at a glance. */
  letter-spacing: 0.0892857143em;
  text-transform: uppercase;
}

/* Flush variant: keeps the hover layer, the radius and the vertical rhythm, drops only the inset.
 * `margin-inline` rather than left/right, because RTL is enabled. */
.hr-list-add-btn--flush {
  margin-inline: 0;
}

/* Tint the leading icon with the accent role (styling our own element, not a Quasar-internal class). */
.hr-list-add-btn__icon {
  color: var(--app-primary);
}

/*
 * Outlined empty-state slot: a tile-shaped, outlined footprint (no fill) that stands in for the
 * group when it's empty — "a place a tile will go". Still just the trigger; the outline (not fill)
 * keeps it reading as an affordance rather than a record.
 */
.hr-list-add-btn--outlined {
  /* Match a list tile's height so the empty slot reads as one tile's footprint, not an oversized box. */
  min-height: 56px;
  border: 1px solid var(--app-outline-variant);
}
</style>
